import React, { memo } from "react";

import { transformDateToLocalDateString } from "helpers";
import { ShowcaseCard, ShowcaseNotice } from "ui/components/Showcase";
import CheckIcon from "ui/modules/fleet/vehicles/pages/VehicleDetailPage/components/VehicleDetailCard/components/VehicleEditCard/components/VehicleWizardIntro/scenes/CheckIcon";

import { SERVICE_INTRO_SAMPLE, SERVICE_STATUS_STEPS } from "../consts";
import { ServiceSceneProps } from "../types";

/** Korak "Osnovne informacije": kartica naloga s vozilom i rokovima, tijek naloga i status vozila. */
const ServiceInfoScene: React.FC<ServiceSceneProps> = ({ formValues }) => {
  const name = formValues.name || SERVICE_INTRO_SAMPLE.name;
  const km = (formValues.kmTraveled ?? SERVICE_INTRO_SAMPLE.kmTraveled).toLocaleString("hr-HR");
  const receivedDate = transformDateToLocalDateString(formValues.receivedDate ?? new Date());
  const expectedDate = formValues.expectedDate ? transformDateToLocalDateString(formValues.expectedDate) : "Rok servisa";
  const currentStatusIndex = Math.max(
    SERVICE_STATUS_STEPS.findIndex((status) => status.id === formValues.vehicleServiceStatusId),
    0
  );
  const currentStatus = SERVICE_STATUS_STEPS[currentStatusIndex];
  const damageCount = formValues.damageIds.length;

  return (
    <>
      <ShowcaseCard className="left-[0.5rem] top-[-1rem] w-[17rem] h-[18rem]" delay={0}>
        <div className="p-5">
          {/* Slika vozila ima širok prozirni rub, pa se uvećava unutar okvira da auto ispuni karticu. */}
          <div className="h-[8.5rem] rounded-md bg-gradient-to-br from-slate-50 to-slate-100 overflow-hidden">
            <img src={SERVICE_INTRO_SAMPLE.image} alt="" className="w-full h-full object-contain scale-[1.6]" />
          </div>

          <div className="mt-3 flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-slate-400">Servisni nalog</div>
              <div className="text-sm font-bold text-slate-800 truncate">{name}</div>
            </div>
            <span className="px-2 py-0.5 rounded-full text-[0.65rem] font-semibold bg-amber-100 text-amber-700 flex-shrink-0">
              {currentStatus.name}
            </span>
          </div>

          <div className="mt-3 flex items-center justify-between gap-2 rounded-md bg-slate-50 px-2 py-1.5 text-[0.65rem]">
            <span className="font-semibold text-slate-700">{receivedDate}</span>
            <span className="flex-1 h-px bg-slate-300" />
            <span className="text-slate-400">{expectedDate}</span>
          </div>
          <div className="mt-2 text-[0.65rem] text-slate-400">{km} km</div>
        </div>
      </ShowcaseCard>

      <ShowcaseCard className="right-[-0.5rem] top-[6rem] w-[14rem]" delay={0.25}>
        <div className="p-5">
          <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-slate-400">Tijek naloga</div>
          <div className="mt-3 space-y-2">
            {SERVICE_STATUS_STEPS.map((status, index) => (
              <div key={status.id} className="flex items-center gap-2">
                {index <= currentStatusIndex ? (
                  <CheckIcon />
                ) : (
                  <span className="w-5 h-5 rounded-full border-2 border-dashed border-slate-300 flex-shrink-0" />
                )}
                <span
                  className={
                    index <= currentStatusIndex
                      ? "text-[0.7rem] font-semibold text-slate-700"
                      : "text-[0.7rem] text-slate-400"
                  }
                >
                  {status.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      </ShowcaseCard>

      <ShowcaseCard className="left-[-1.5rem] bottom-[-0.5rem] w-[15rem]" delay={0.5}>
        <ShowcaseNotice
          title="Vozilo je na servisu"
          subtitle={damageCount > 0 ? `Rješava ${damageCount} štete` : "Nedostupno do preuzimanja"}
          iconClassName="bg-amber-100"
          icon={
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="#B45309" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" />
            </svg>
          }
        />
      </ShowcaseCard>
    </>
  );
};

export default memo(ServiceInfoScene);
